<template>
	<div>
		<q-modal
			v-model="add_question"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			@show="clearQuestions()"
			@hide="hideQuestionModal()"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="hideQuestionModal()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('question') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm">
						<div class="col-xs-10 col-sm-10">
							<p class="caption">{{ $t('title') }}</p>
						</div>

						<div class="col-xs-2 col-sm-2">
							<q-btn
								small
								@click="showQuestionsDataBaseModal()"
								:label="$t('import_questions_database')"
								v-if="id_question === 0"
							/>
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-editor
								v-model="title"
								:error="$v.title.$error"
								@input="$v.title.$touch"
								:toolbar="[
									['bold', 'italic', 'strike', 'underline'],
									['left', 'center', 'right', 'justify'],
									['link', 'upload'],
									[
										{
											label: null,
											icon: 'fas fa-font',
											fixedLabel: true,
											fixedIcon: true,
											list: 'no-icons',
											options: [
												'p',
												'h1',
												'h2',
												'h3',
												'h4',
												'h5',
												'h6',
												'code',
											],
										},
										{
											label: null,
											icon: 'fas fa-text-height',
											fixedLabel: true,
											fixedIcon: true,
											list: 'no-icons',
											options: [
												'size-1',
												'size-2',
												'size-3',
												'size-4',
												'size-5',
												'size-6',
												'size-7',
											],
										},
										'removeFormat',
									],
									['quote', 'unordered', 'ordered'],
								]"
								:definitions="{
									bold: { label: null, icon: 'fas fa-bold' },
									italic: { label: null, icon: 'fas fa-italic' },
									strike: { label: null, icon: 'fas fa-strikethrough' },
									underline: { label: null, icon: 'fas fa-underline' },
									left: { label: null, icon: 'fas fa-align-left' },
									center: { label: null, icon: 'fas fa-align-center' },
									right: { label: null, icon: 'fas fa-align-right' },
									justify: { label: null, icon: 'fas fa-align-justify' },
									link: { label: null, icon: 'fas fa-link' },
									upload: {
										tip: null,
										icon: 'fas fa-file-image',
										handler: insertImg,
									},
									removeFormat: { label: null, icon: 'fas fa-eraser' },
									quote: { label: null, icon: 'fas fa-quote-right' },
									unordered: { label: null, icon: 'fas fa-list-ul' },
									ordered: { label: null, icon: 'fas fa-list-ol' },
								}"
							>
							</q-editor>
						</div>
					</div>

					<q-table
						:data="alternatives"
						:columns="columns"
						:pagination.sync="server_pagination"
						@request="request_alternatives"
						:loading="loading"
						row-key="id"
						:no-data-label="$t('no_data_avaliable')"
						:no-results-label="$t('no_results_found')"
						:loading-label="$t('loading_data')"
						:rows-per-page-label="$t('items_per_page')"
						:rows-per-page-options="[0]"
						binary-state-sort
						class="q-mt-lg"
					>
						<template slot="top" slot-scope="props" :props="props">
							<div class="q-table-title">{{ $t('alternatives') }}</div>
							<div class="col" />
							<q-btn
								color="secondary"
								icon="far fa-plus"
								:label="$t('new_f')"
								@click="addAlternative()"
								:disable="has_results"
							/>
						</template>

						<q-tr slot="header" slot-scope="props" :props="props">
							<q-th v-for="col in props.cols" :key="col.name" :props="props">
								{{ $t(col.label) }}
							</q-th>
						</q-tr>

						<q-tr
							slot="body"
							slot-scope="props"
							:props="props"
							@click="addAlternative()"
						>
							<q-td auto-width key="is_correct" :props="props">
								<q-radio
									v-model="props.row.is_correct"
									:val="true"
									@input="changeCorrect(props.row)"
									:disable="has_results"
								/>
							</q-td>

							<q-td key="title" :props="props">
								{{ props.row.title.substring(0, 100) }}
							</q-td>

							<q-td auto-width key="actions" :props="props">
								<q-btn
									flat
									round
									color="primary"
									icon="far fa-pencil-alt"
									@click="editAlternative(props.row)"
								>
									<q-tooltip>
										{{ $t('view_or_edit_tooltip') }}
									</q-tooltip>
								</q-btn>

								<q-btn
									flat
									round
									color="negative"
									icon="far fa-trash-alt"
									@click="removeAlternative(props.row)"
									:disable="has_results"
								>
									<q-tooltip>
										{{ $t('delete_tooltip') }}
									</q-tooltip>
								</q-btn>
							</q-td>
						</q-tr>
					</q-table>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-12">
							<p class="caption">{{ $t('mandatory') }}</p>
							<q-btn-toggle
								v-model="is_mandatory"
								:toggle-color="getColorIsMandatoryButton"
								:options="is_mandatory_options"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('cancel')"
								icon="far fa-block"
								@click="hideQuestionModal()"
							/>
						</div>

						<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
							<q-btn
								color="secondary"
								class="full-width"
								:label="$t('save')"
								icon="far fa-save"
								@click="saveQuestion()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog
			v-model="add_alternative"
			preventClose
			@ok="saveAlternative()"
			@hide="clearAlternative()"
		>
			<span slot="title">{{ $t('add_alternative') }}</span>

			<span slot="message">{{ $t('alternative') }}</span>

			<span slot="body">
				<q-editor
					v-model="new_alternative"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						upload: { tip: null, icon: 'fas fa-file-image', handler: insertImg },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
				</q-editor>
			</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="tertiary" :label="$t('cancel')" @click="add_alternative = false" />
				<q-btn color="positive" :label="$t('add_alternative')" @click="saveAlternative()" />
			</template>
		</q-dialog>

		<q-dialog v-model="edit_alternative" preventClose @hide="clearAlternative()">
			<span slot="title">{{ $t('edit_alternative') }}</span>

			<span slot="message">{{ $t('alternative') }}</span>
			<span slot="body">
				<q-editor
					v-model="alternative.title"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						upload: { tip: null, icon: 'fas fa-file-image', handler: insertImg },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
				</q-editor>
			</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn
					color="positive"
					:label="$t('edit_alternative')"
					@click="saveEditAlternative()"
				/>
			</template>
		</q-dialog>

		<q-modal
			v-model="search_questions_database_modal"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			@show="clearSelectedQuestion()"
			@hide="clearSelectedQuestion()"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn
						flat
						round
						dense
						@click="search_questions_database_modal = false"
						icon="fas fa-times"
					/>
					<q-toolbar-title>
						{{ $t('question') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-sm">
						<div class="col-xs-12 col-sm-12">
							<q-input
								v-model="search_questions_list"
								:float-label="
									$t('titles') +
										': ' +
										$t('training') +
										', ' +
										$t('test') +
										', ' +
										$t('question')
								"
								clearable
								@clear="clearSelectedQuestion()"
							>
								<q-autocomplete
									@search="searchQuestionsDataBase"
									@selected="selectQuestionDataBase"
									:max-results="25"
									:min-characters="3"
									:value-field="
										(v) => `${v.id} - ${v.live_class_test_question_title}`
									"
								/>
							</q-input>
						</div>
					</div>

					<div
						class="row gutter-sm q-mt-sm"
						v-if="selected_question.live_class_title !== ''"
					>
						<div class="col-xs-12 col-sm-12">
							<strong>{{ $t('training') }}: </strong
							>{{ selected_question.live_class_title }}
						</div>

						<div class="col-xs-12 col-sm-12">
							<strong>{{ $t('test') }}: </strong
							>{{ selected_question.live_class_test_title }}
						</div>

						<div class="col-xs-12 col-sm-12">
							<strong>{{ $t('question') }}: </strong
							>{{ selected_question.live_class_test_question_title }}
						</div>

						<div class="col-xs-12 col-sm-12">
							<q-table
								:data="selected_question.alternatives"
								:columns="select_question_columns"
								row-key="id"
								binary-state-sort
								class="q-mt-lg"
							>
								<template slot="top">
									<div class="q-table-title">{{ $t('alternatives') }}</div>
									<div class="col" />
								</template>

								<q-tr slot="header" slot-scope="props" :props="props">
									<q-th v-for="col in props.cols" :key="col.name" :props="props">
										{{ $t(col.label) }}
									</q-th>
								</q-tr>

								<q-tr
									slot="body"
									slot-scope="props"
									:props="props"
									@click="addAlternative()"
								>
									<q-td auto-width key="is_correct" :props="props">
										<q-radio
											v-model="props.row.is_correct"
											:val="true"
											@input="changeCorrect(props.row)"
											:disable="true"
										/>
									</q-td>

									<q-td key="title" :props="props">
										{{ props.row.title.substring(0, 100) }}
									</q-td>
								</q-tr>
							</q-table>
						</div>
					</div>

					<div class="row gutter-sm  q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								icon="far fa-block"
								:label="$t('cancel')"
								@click="search_questions_database_modal = false"
							/>
						</div>

						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="primary"
								class="full-width"
								icon="far fa-file-import"
								:label="$t('import')"
								@click="confirmSelectedQuestion()"
								:disable="selected_question.live_class_test_question_title === ''"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import * as moment from 'moment'
import { BlobServiceClient } from '@azure/storage-blob'

export default {
	props: ['id_live_class', 'id_test', 'id_question', 'has_results', 'add_question'],

	i18nOptions: { namespaces: ['custom', 'common', 'training', 'admin'] },

	data() {
		return {
			add_alternative: false,
			alternative: {
				id: '',
				title: '',
			},
			alternatives: [],
			cancel_token: '',
			columns: [
				{
					align: 'center',
					field: 'is_correct',
					label: 'correct',
					name: 'is_correct',
					required: true,
					sortable: false,
				},
				{
					align: 'left',
					field: 'title',
					label: 'alternative',
					name: 'title',
					required: true,
					sortable: false,
				},
				{
					align: 'left',
					field: 'actions',
					label: '',
					name: 'actions',
					required: true,
					sortable: false,
				},
			],
			crop_modal: false,
			edit_alternative: false,
			fileUrl: '',
			is_mandatory: true,
			is_mandatory_options: [
				{
					label: this.$t('yes'),
					value: true,
				},
				{
					label: this.$t('no'),
					value: false,
				},
			],
			loading: false,
			new_alternative: '',
			original_image: '',
			search_questions_database_modal: false,
			search_questions_list: [],
			select_question_columns: [
				{
					align: 'center',
					field: 'is_correct',
					label: 'correct',
					name: 'is_correct',
					required: true,
					sortable: false,
				},
				{
					align: 'left',
					field: 'title',
					label: 'alternative',
					name: 'title',
					required: true,
					sortable: false,
				},
			],
			selected_question: {
				alternatives: [],
				live_class_test_question_title: '',
				live_class_test_title: '',
				live_class_title: '',
			},
			server_pagination: {
				descending: false,
				page: 1,
				rowsNumber: 0,
				rowsPerPage: 0,
				sortBy: null,
			},
			source: '',
			title: '',
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getMaxWidth() {
			return this.$q.platform.is.mobile ? '100vw' : '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		getColorIsMandatoryButton() {
			return this.is_mandatory === true ? 'positive' : 'negative'
		},
	},

	methods: {
		request_alternatives({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				ordering: order_by,
			}

			this.$axios
				.get(
					`/admin_live_classes/live_classes/${this.id_live_class}/tests/${
						this.id_test
					}/questions/${this.id_question}/?${this.$qs.stringify(data)}`,
					this.getHeader
				)
				.then((response) => {
					this.alternatives = []
					this.title = response.data.title

					response.data.live_class_test_question_answers.forEach((result, index) => {
						this.alternatives.push({
							title: result.title,
							is_correct: result.is_correct,
							actions: index,
							id: result.id,
						})
					})

					this.server_pagination.rowsNumber = this.alternatives.length

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		loadQuestion() {
			this.$q.loading.show()

			this.request_alternatives({ pagination: this.server_pagination })

			this.$axios
				.get(
					`/admin_live_classes/live_classes/${this.id_live_class}/tests/${
						this.id_test
					}/questions/${this.id_question}/`,
					this.getHeader
				)
				.then((response) => {
					this.title = response.data.title
					this.is_mandatory = response.data.is_mandatory

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		saveQuestion() {
			this.$v.$touch()

			if (!this.$v.$error && this.alternatives.length > 1) {
				this.$q.loading.show()

				let data = {
					title: this.title,
					is_mandatory: this.is_mandatory,
					live_class_test_question_answers: this.alternatives,
				}

				if (this.id_question != 0) {
					this.$axios
						.put(
							`/admin_live_classes/live_classes/${this.id_live_class}/tests/${
								this.id_test
							}/questions/${this.id_question}/`,
							data,
							this.getHeader
						)
						.then(() => {
							this.$q.loading.hide()
							this.$v.$reset()

							this.$q.notify({
								message: this.$t('question_edit_success'),
								type: 'positive',
								icon: 'fal fa-save',
							})

							this.hideQuestionModal()
						})
						.catch((e) => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('question_edit_error'),
								type: 'negative',
								icon: 'fal fa-ban',
							})

							console.log(e)
						})
				} else {
					this.$axios
						.post(
							`/admin_live_classes/live_classes/${this.id_live_class}/tests/${
								this.id_test
							}/questions/`,
							data,
							this.getHeader
						)
						.then(() => {
							this.$q.loading.hide()
							this.$v.$reset()

							this.hideQuestionModal()
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
						})
				}
			}
		},

		addAlternative() {
			this.add_alternative = true
		},

		setFocus() {
			this.$refs.new_alternative.$el.focus()
		},

		saveAlternative() {
			if (this.new_alternative) {
				let is_unique = true
				let is_correct = false

				this.alternatives.forEach((alternative) => {
					if (alternative.title == this.new_alternative) is_unique = false
				})

				if (is_unique) {
					if (this.alternatives.length == 0) {
						is_correct = true
					}

					this.alternatives.push({
						title: this.new_alternative,
						is_correct: is_correct,
						actions: this.alternatives.length == 0 ? 0 : this.alternatives.length + 1,
					})
				}

				this.add_alternative = false
			} else {
				this.add_alternative = false
			}
			this.server_pagination.rowsNumber++
		},

		removeAlternative(row) {
			if (this.alternatives[row.__index].is_correct) {
				this.alternatives.splice(row.__index, 1)

				if (this.alternatives.length > 0) {
					this.alternatives[0].is_correct = true
				}
			} else {
				this.alternatives.splice(row.__index, 1)
			}

			this.server_pagination.rowsNumber--
		},

		clearQuestions() {
			this.$v.$reset()
		},

		clearAlternative() {
			this.new_alternative = ''
		},

		changeCorrect(row) {
			this.alternatives.forEach((alternative) => {
				if (alternative.actions != row.actions) alternative.is_correct = false
			})
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		hideQuestionModal() {
			this.title = ''
			this.alternatives = []
			this.$v.$reset()
			this.server_pagination.rowsNumber = 0
			this.is_mandatory = true
			this.$emit('returnToQuestions', '')
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media/html_images/live_class/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {})
								.then(() => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											let data = {
												file_url: response.data.fileUrl,
											}

											this.$axios
												.post('/utils/presigned_url/', data, this.getHeader)
												.then((response) => {
													document.execCommand(
														'insertHTML',
														true,
														`<div><img class="uploadBase" src="${
															response.data.file_url
														}" style="max-width: 100%" /></div>`
													)
												})
												.catch((e) => {
													console.log(e)
												})
												.finally(() => {
													this.$q.loading.hide()
												})
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},

		editAlternative(item) {
			this.alternative = item
			this.edit_alternative = true
		},

		saveEditAlternative() {
			this.edit_alternative = false
		},

		showQuestionsDataBaseModal() {
			this.search_questions_database_modal = true
		},

		searchQuestionsDataBase(terms, done) {
			this.$axios
				.post(
					`/admin_live_classes/live_classes/${this.id_live_class}/tests/${
						this.id_test
					}/questions/search_questions/`,
					{ search_question: terms },
					this.getHeader
				)
				.then((response) => {
					const questions = []
					response.data.forEach((element) => {
						let regEx = new RegExp(terms, 'ig')
						let replaceMask = '<u>' + terms + '</u>'
						let live_class_test_question_title = element.live_class_test_question_title.replace(
							regEx,
							replaceMask
						)
						let live_class_test_title = element.live_class_test_title.replace(
							regEx,
							replaceMask
						)
						let live_class_title = element.live_class_title.replace(regEx, replaceMask)
						let lb_question = this.$t('question')
						let lb_questions_quantity = this.$t('questions_quantity')
						let lb_test = this.$t('test')
						let lb_live_class = this.$t('training')
						let option_label = `
                            <strong>${lb_question}: </strong>${live_class_test_question_title}<br>
                            <strong>${lb_questions_quantity}: </strong>${
							element.live_class_test_question_alternatives_qty
						}<br>
                            <strong>${lb_live_class}: </strong>${live_class_title}<br>
                            <strong>${lb_test}: </strong>${live_class_test_title}
                        `
						questions.push({
							value: element.id,
							live_class_title: element.live_class_title,
							live_class_test_title: element.live_class_test_title,
							live_class_test_question_title: element.live_class_test_question_title,
							label: option_label,
							id: element.id,
							alternatives: element.alternatives,
						})
					})
					done(questions)
				})
				.catch((e) => {
					done([])
					console.log(e)
				})
		},

		selectQuestionDataBase(item, keyboard) {
			if (!keyboard) {
				this.selected_question.live_class_title = item.live_class_title
				this.selected_question.live_class_test_title = item.live_class_test_title
				this.selected_question.live_class_test_question_title =
					item.live_class_test_question_title
				this.selected_question.alternatives = item.alternatives
			}
		},

		confirmSelectedQuestion() {
			this.title = this.selected_question.live_class_test_question_title
			this.alternatives = this.selected_question.alternatives.map((alt) => {
				return {
					...alt,
					actions: alt.__index,
				}
			})
			this.clearSelectedQuestion()
			this.search_questions_database_modal = false
		},

		clearSelectedQuestion() {
			this.selected_question = {
				live_class_title: '',
				live_class_test_title: '',
				live_class_test_question_title: '',
				alternatives: [],
			}
			this.search_questions_list = ''
		},
	},

	watch: {
		id_question: function() {
			if (this.id_question != 0) this.loadQuestion()
		},
	},

	validations() {
		return {
			title: { required },
		}
	},
}
</script>

<style></style>
